// SCR-STAFF-208 — شبیه‌ساز قیمت (US-CAT-072؛ AP-CAT-03): سبد خدمات و پکیج با مراجعه‌کننده فرضی ← قیمت پایه، تخفیف‌های اعمال‌شده، مالیات
// تخمینی و مبلغ نهایی با توضیح هر قاعده اعمال‌شده یا ردشده؛ همان موتور تخفیف قیمت‌گذاری واقعی بدون رزرو بودجه. «تبدیل به برآورد هزینه»
// سبد و کوپن را به ایجاد برآورد BIL (SCR-STAFF-407، AP-BIL-03) می‌برد (C-AGENT-704، C-AGENT-825).
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { unwrap, type PriceSimulation, type SimulatedLine } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, ListPage, Money, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { QuoteDraftState } from '../billing/QuotesPage';
import { catKeys, useActiveServices } from './common';

export const SCR_STAFF_208 = 'SCR-STAFF-208';

interface BasketLine {
  key: number;
  serviceId: string | null;
  packageId: string | null;
  quantity: string;
}

export function SimulatorPage() {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const services = useActiveServices();
  const access = useAccess();
  const navigate = useNavigate();
  const packages = useQuery({
    queryKey: catKeys.packages(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/packages')),
  });
  const [lines, setLines] = useState<BasketLine[]>([
    { key: 1, serviceId: null, packageId: null, quantity: '1' },
  ]);
  const [gender, setGender] = useState('');
  const [firstVisit, setFirstVisit] = useState(false);
  const [coupon, setCoupon] = useState('');
  const [result, setResult] = useState<PriceSimulation | null>(null);
  const publishedPackages = (packages.data?.items ?? []).filter(
    (item) => item.publishedVersion && item.status === 'Published',
  );

  const simulate = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/catalog/pricing:simulate', {
          body: {
            lines: lines
              .filter((line) => line.serviceId || line.packageId)
              .map((line) => ({
                serviceId: line.serviceId,
                packageId: line.packageId,
                quantity: Math.max(1, Number(line.quantity) || 1),
                providerId: null,
              })),
            hypothetical: { gender: gender || null, firstVisit, birthDate: null },
            codes: coupon.trim() ? [coupon.trim()] : [],
            branchId: session?.activeBranchId ?? null,
            channel: null,
            at: null,
            patientId: null,
            selectedRuleIds: null,
            activeMembershipPlanIds: null,
          },
        }),
      ),
    onSuccess: (data) => setResult(data),
  });

  const update = (key: number, change: Partial<BasketLine>) =>
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...change } : line)));
  const add = (kind: 'service' | 'package') =>
    setLines((current) => [
      ...current,
      {
        key: Math.max(0, ...current.map((line) => line.key)) + 1,
        serviceId: null,
        packageId: kind === 'package' ? '' : null,
        quantity: '1',
      },
    ]);

  const nameOf = (row: SimulatedLine) =>
    row.packageId
      ? (publishedPackages.find((item) => item.id === row.packageId)?.name ?? row.packageId)
      : services.name(row.serviceId);

  const columns: DataTableColumn<SimulatedLine>[] = [
    {
      id: 'item',
      headerKey: 'cat:simulator.columns.item',
      cell: (row) => `${nameOf(row)} × ${faDigits(row.quantity)}`,
    },
    {
      id: 'gross',
      headerKey: 'cat:simulator.columns.gross',
      cell: (row) => (row.errorCode ? t('cat:simulator.no_price') : <Money amountMinor={row.gross.amount} />),
    },
    {
      id: 'discount',
      headerKey: 'cat:simulator.columns.discount',
      cell: (row) => (
        <span data-testid={`sim-discount-${row.lineId}`}>
          <Money amountMinor={row.discount.amount} />
        </span>
      ),
    },
    {
      id: 'tax',
      headerKey: 'cat:simulator.columns.tax',
      cell: (row) => <Money amountMinor={row.tax.amount} />,
    },
    {
      id: 'payable',
      headerKey: 'cat:simulator.columns.payable',
      cell: (row) => <Money amountMinor={row.payable.amount} />,
    },
  ];

  return (
    <ListPage screenId={SCR_STAFF_208} titleKey="cat:simulator.title" subtitleKey="cat:simulator.subtitle">
      <Stack spacing={2}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle1" component="h2">
              {t('cat:simulator.lines')}
            </Typography>
            {lines.map((line, index) => (
              <Stack key={line.key} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                {line.packageId === null ? (
                  <Autocomplete
                    fullWidth
                    options={services.options.map((option) => option.id)}
                    value={line.serviceId}
                    onChange={(_, value) => update(line.key, { serviceId: value })}
                    getOptionLabel={(id) => services.name(id)}
                    filterOptions={(options) => options}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input') {
                        services.search(value);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('cat:simulator.service')}
                        slotProps={{
                          htmlInput: { ...params.inputProps, 'data-testid': `sim-service-${index}` },
                        }}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    select
                    fullWidth
                    label={t('cat:simulator.package')}
                    value={line.packageId}
                    onChange={(event) => update(line.key, { packageId: event.target.value })}
                    slotProps={{ htmlInput: { 'data-testid': `sim-package-${index}` } }}
                  >
                    {publishedPackages.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  type="number"
                  label={t('cat:simulator.quantity')}
                  value={line.quantity}
                  onChange={(event) => update(line.key, { quantity: event.target.value })}
                  slotProps={{ htmlInput: { min: 1, max: 1000, 'data-testid': `sim-quantity-${index}` } }}
                  sx={{ width: 120 }}
                />
                <IconButton
                  aria-label={t('cat:simulator.remove')}
                  disabled={lines.length === 1}
                  onClick={() => setLines((current) => current.filter((item) => item.key !== line.key))}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            ))}
            <Stack direction="row" spacing={1}>
              <Button onClick={() => add('service')} data-testid="sim-add-service">
                {t('cat:simulator.add_line')}
              </Button>
              <Button
                onClick={() => add('package')}
                data-testid="sim-add-package"
                disabled={publishedPackages.length === 0}
              >
                {t('cat:simulator.add_package')}
              </Button>
            </Stack>
            <Typography variant="subtitle1" component="h2">
              {t('cat:simulator.patient')}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <TextField
                select
                label={t('cat:simulator.gender')}
                value={gender}
                onChange={(event) => setGender(event.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">{t('cat:discounts.form.any')}</MenuItem>
                {(['Female', 'Male', 'Other'] as const).map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`cat:discounts.gender.${value}`)}
                  </MenuItem>
                ))}
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={firstVisit}
                    onChange={(event) => setFirstVisit(event.target.checked)}
                    slotProps={{ input: { 'data-testid': 'sim-first-visit' } as object }}
                  />
                }
                label={t('cat:simulator.first_visit')}
              />
              <TextField
                label={t('cat:simulator.coupon')}
                value={coupon}
                onChange={(event) => setCoupon(event.target.value)}
                slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'sim-coupon' } }}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => simulate.mutate()}
                disabled={simulate.isPending || !lines.some((line) => line.serviceId || line.packageId)}
                data-testid="sim-run"
              >
                {t('cat:simulator.run')}
              </Button>
              {access.can('bil.quote.create') ? (
                <Button
                  disabled={!lines.some((line) => line.serviceId || line.packageId)}
                  onClick={() => {
                    const quoteDraft: QuoteDraftState = {
                      source: 'Simulator',
                      lines: lines
                        .filter((line) => line.serviceId || line.packageId)
                        .map((line) => ({ serviceId: line.serviceId, packageId: line.packageId || null, quantity: Math.max(1, Number(line.quantity) || 1) })),
                      codes: coupon.trim() ? [coupon.trim()] : [],
                    };
                    void navigate('/quotes/new', { state: { quoteDraft } });
                  }}
                  data-testid="sim-to-quote"
                >
                  {t('cat:simulator.to_quote')}
                </Button>
              ) : (
                <Tooltip title={t('cat:simulator.to_quote_denied')}>
                  <span>
                    <Button disabled>{t('cat:simulator.to_quote')}</Button>
                  </span>
                </Tooltip>
              )}
            </Stack>
            {simulate.error ? <ErrorState error={simulate.error} compact /> : null}
          </Stack>
        </Paper>

        {result && (
          <Stack spacing={2} data-testid="sim-result">
            <DataTable
              captionKey="cat:simulator.results_caption"
              columns={columns}
              rows={result.lines}
              getRowId={(row) => row.lineId}
              emptyTitleKey="cat:simulator.empty"
            />
            <Typography variant="subtitle1" data-testid="sim-total">
              {t('cat:simulator.total')}: <Money amountMinor={result.gross.amount} /> −{' '}
              <Money amountMinor={result.discount.amount} /> + <Money amountMinor={result.tax.amount} /> ={' '}
              <Money amountMinor={result.payable.amount} />
            </Typography>
            {result.capApplied && <Alert severity="info">{t('cat:simulator.cap_applied')}</Alert>}
            <Paper variant="outlined">
              <Typography variant="subtitle2" sx={{ px: 2, pt: 1 }} component="h2">
                {t('cat:simulator.explanations')}
              </Typography>
              <List dense>
                {result.explanations.map((item) => (
                  <ListItem key={item.ruleId} data-testid={`sim-explanation-${item.ruleCode}`}>
                    <ListItemText
                      primary={`${item.ruleName} — ${t(`cat:discount_reasons.${item.reason}`)}`}
                      secondary={
                        item.applied ? <Money amountMinor={item.amount.amount} /> : (item.detail ?? undefined)
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
            {result.invalidCodes.length > 0 && (
              <Alert severity="warning" data-testid="sim-invalid-codes">
                {t('cat:simulator.invalid_codes')}:{' '}
                {result.invalidCodes
                  .map((item) => `${item.code} (${t(`cat:discount_reasons.${item.reason}`)})`)
                  .join(t('cat:list_separator'))}
              </Alert>
            )}
          </Stack>
        )}
      </Stack>
    </ListPage>
  );
}
